import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CardV2 as Card, openMultiStepModal, showError, toast } from '@feathr/components';
import { useFundraiser } from '@feathr/extender/hooks';
import crmIcon from '@feathr/extender/images/integrations/crm_icon.svg';

import ConnectState from '../ConnectState';
import { useCRMConnectModal } from './CRMConnectModal';
import getCRMConnectSteps from './CRMConnectModal/';
import { logos } from './CRMConnectModal/assets';

import * as styles from './CRMConnectCard.css';

function CRMConnectCard(): JSX.Element {
  const { t } = useTranslation();
  const { openModal, closeModal, isOpen } = useCRMConnectModal();
  const { fundraiser, isPending, state: stripeState, crm, disconnectCRM } = useFundraiser();
  const crmVendor = crm?._cls;
  const crmState = crm?.state;
  const crmActive = crmState === 'connected';

  function getFormalNameCRM(): string {
    if (crmVendor === 'BlackbaudCRM') {
      return t("Blackbaud Raiser's Edge NXT");
    }
    if (crmVendor === 'SalesforceCRM') {
      return t('Salesforce');
    }
    return t('Unknown CRM');
  }

  async function onComplete(): Promise<void> {
    if (!crmVendor) {
      return;
    }
    try {
      await fundraiser?.patchIntegration({
        // Only PATCH the necessary CRM fields
        crm: {
          _cls: crm._cls,
          client_id: crm.client_id,
          client_secret: crm.client_secret,
          domain: crm.domain,
        },
      });
      toast(
        t('{{- crmMessage}} configuration saved', {
          crmMessage: getFormalNameCRM(),
        }),
        { type: 'success' },
      );
    } catch (error) {
      showError(error, t);
    } finally {
      closeModal();
    }
  }

  async function handleOnClickDisconnect(): Promise<void> {
    if (!crm) {
      return;
    }
    try {
      disconnectCRM();
      toast(
        t('{{- crmMessage}} disconnected', {
          crmMessage: getFormalNameCRM(),
        }),
        { type: 'success' },
      );
    } catch (error) {
      showError(error, t);
    }
  }

  const steps = useMemo(
    () => getCRMConnectSteps({ fundraiser, logos, t }),
    [fundraiser, fundraiser?.isDirty, fundraiser?.toJS()],
  );

  useEffect(() => {
    if (isOpen) {
      openMultiStepModal(steps, onComplete, closeModal);
    }
  }, [isOpen, onComplete, closeModal]);

  function getDescription(): JSX.Element {
    let content: ReactNode;
    if (crmActive) {
      content = t('Data sync active');
    } else {
      content = (
        <>
          {t('Connect your CRM to sync donation data.')}
          <Button
            href={
              'https://help.feathr.co/hc/en-us/articles/36120664372375-How-to-Connect-Feathr-Fundraising-with-Salesforce'
            }
            target={'_blank'}
            type={'link'}
          >
            {t('Learn more about CRM integrations')}
          </Button>
        </>
      );
    }
    return <div className={styles.description}>{content}</div>;
  }

  function getPrefix(): JSX.Element {
    if (crmActive && crmVendor) {
      const cardIcon = logos[`${crmVendor}_card`];
      return (
        <img
          alt={t('{{- crmVendor}} logo', { crmVendor })}
          className={styles.icon}
          src={cardIcon}
        />
      );
    }
    return (
      <img
        alt={t('Database icon with three stacked discs on a light gray background.')}
        className={styles.icon}
        src={crmIcon}
      />
    );
  }

  function getTitle(): string {
    if (crm && crmActive) {
      return getFormalNameCRM();
    }

    return t('Customer Relationship Manager');
  }

  function getTitleSuffix(): string | null {
    if (crmActive) {
      return null;
    }
    return t('(Optional)');
  }

  function getCRMButton(): JSX.Element {
    if (crmActive) {
      return (
        <Button disabled={isPending} isLoading={isPending} onClick={handleOnClickDisconnect}>
          {t('Disconnect')}
        </Button>
      );
    }

    return (
      <Button disabled={isPending || stripeState === 'not_connected'} onClick={openModal}>
        {t('Connect CRM')}
      </Button>
    );
  }

  return (
    <Card name={'crm-connect-card'} width={'full'}>
      <Card.Header
        description={getDescription()}
        prefix={getPrefix()}
        title={getTitle()}
        titleSuffix={getTitleSuffix()}
      >
        <ConnectState state={crmState} />
        {getCRMButton()}
      </Card.Header>
    </Card>
  );
}

export default observer(CRMConnectCard);
